import { API_BASE, getApiAuthHeaders } from '$lib/constants/api';
import { clearAccessToken, refreshSession } from '$lib/auth';

function getFilenameFromContentDisposition(header: string | null): string | null {
	if (!header) return null;
	const match = header.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
	if (match && match[1]) {
		return match[1].replace(/['"]/g, '');
	}
	return null;
}

export async function downloadFirmwareMappings(unitId: number, format: string): Promise<void> {
	const url = new URL(`${API_BASE}/units/${unitId}/download_firmware/`);
	url.searchParams.set('export_format', format);

	let retried = false;
	while (true) {
		const response = await fetch(url, {
			method: 'GET',
			credentials: 'include',
			headers: getApiAuthHeaders()
		});

		if (response.status === 401 && !retried) {
			try {
				await refreshSession();
				retried = true;
				continue;
			} catch {
				clearAccessToken();
				throw new Error('Authentication required. Please log in again.');
			}
		}

		if (response.status === 403) {
			throw new Error('You do not have permission to export mappings for this unit.');
		}

		if (response.status === 400) {
			const errorData = await response.json().catch(() => ({}));
			const err = errorData as { error?: string };
			throw new Error(err.error || 'Invalid request parameters.');
		}

		if (!response.ok) {
			throw new Error(`Server error: ${response.status}`);
		}

		const contentDisposition = response.headers.get('content-disposition');
		let filename = getFilenameFromContentDisposition(contentDisposition);

		if (!filename) {
			switch (format) {
				case 'v3':
					filename = 'mappings_v3.json';
					break;
				case 'v2_json':
					filename = 'mappings_v2.txt';
					break;
				case 'v2_header':
					filename = 'scene_config.h';
					break;
				case 'all':
					filename = 'firmware_mappings_all.zip';
					break;
				default:
					filename = `firmware_${format}`;
			}
		}

		const blob = await response.blob();
		const blobUrl = window.URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = blobUrl;
		a.download = filename;
		document.body.appendChild(a);
		a.click();
		window.URL.revokeObjectURL(blobUrl);
		document.body.removeChild(a);
		return;
	}
}

export async function fetchFirmwareMappings(unitId: number, exportType = 'v3') {
	const url = new URL(`${API_BASE}/units/${unitId}/download_firmware/`);
	url.searchParams.set('export_format', exportType);

	let retried = false;
	while (true) {
		const response = await fetch(url, {
			method: 'GET',
			headers: getApiAuthHeaders()
		});

		if (response.status === 401 && !retried) {
			try {
				await refreshSession();
				retried = true;
				continue;
			} catch {
				clearAccessToken();
				throw new Error('Authentication required. Please log in again.');
			}
		}

		if (!response.ok) {
			const body = await response.text();
			throw new Error(`Unable to fetch firmware mappings: ${body}`);
		}
		return response;
	}
}

export type RelayMapChannel = {
	channel_number: number;
	load_id: number | null;
	load_name: string | null;
	room_name: string | null;
};

export type RelayMapRCUEntry = {
	rcu_id: number;
	rcu_name: string;
	channels: RelayMapChannel[];
};

export type RelayMapDINEntry = {
	din_module_id: number;
	din_name: string;
	channels: RelayMapChannel[];
};

export type RelayMapResponse = {
	rcus: RelayMapRCUEntry[];
	din_modules: RelayMapDINEntry[];
};

export async function fetchRelayMap(unitId: number): Promise<RelayMapResponse> {
	const url = `${API_BASE}/units/${unitId}/relay_map/`;

	let retried = false;
	while (true) {
		const response = await fetch(url, {
			method: 'GET',
			credentials: 'include',
			headers: getApiAuthHeaders()
		});

		if (response.status === 401 && !retried) {
			try {
				await refreshSession();
				retried = true;
				continue;
			} catch {
				clearAccessToken();
				throw new Error('Authentication required. Please log in again.');
			}
		}

		if (!response.ok) {
			const body = await response.text();
			throw new Error(`Unable to fetch relay map: ${body}`);
		}

		return (await response.json()) as RelayMapResponse;
	}
}
